import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { startPasswordReset } from '../../../services/auth';
import { clearMessage } from '../../../reducers/message';
import LiquidGlassCard from '../LiquidGlassCard';
import PopupMessage from '../PopupMessage';
import '../LoginCard/index.css';
import '../AuthSupportCard.css';
import { EMAIL_PATTERN } from '../../../constants/authValidation';


const ForgotPasswordCard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [message, setMessage] = useState('');
  const [popupTrigger, setPopupTrigger] = useState(0);

  useEffect(() => {
    dispatch(clearMessage());
    return () => {
      dispatch(clearMessage());
    };
  }, [dispatch]);

  const showPopup = (nextMessage) => {
    setMessage(nextMessage);
    setPopupTrigger((prev) => prev + 1);
  };

  const normalizeEmail = () => email.trim().toLowerCase();

  const getValidationMessage = () => {
    const normalizedEmail = normalizeEmail();
    if (!normalizedEmail) {
      return t('auth.common.validation.required');
    }
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      return t('auth.common.validation.invalidEmail');
    }
    return null;
  };

  const getRequestErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 429) {
      return t('auth.passwordReset.request.feedback.rateLimited');
    }
    if (status === 400) {
      return t('auth.common.validation.invalidEmail');
    }
    return t('auth.passwordReset.request.feedback.genericError');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationMessage = getValidationMessage();
    if (validationMessage) {
      showPopup(validationMessage);
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedEmail = normalizeEmail();
      await dispatch(startPasswordReset(normalizedEmail));
      setEmail(normalizedEmail);
      setRequestSent(true);
      setMessage('');
    } catch (error) {
      showPopup(getRequestErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {message && (
        <PopupMessage message={message} popupTrigger={popupTrigger} />
      )}

      <LiquidGlassCard
        title={t('auth.passwordReset.request.title')}
        variant="auth"
        size="medium"
        className="login-card"
      >
        {!requestSent ? (
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <p className="auth-support-copy">
              {t('auth.passwordReset.request.descriptions.start')}
            </p>

            <div className="form-group">
              <input
                type="email"
                className="form-control"
                name="email"
                placeholder={t('auth.passwordReset.request.fields.email.placeholder')}
                aria-label={t('auth.passwordReset.request.fields.email.placeholder')}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck="false"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <p className="auth-form-note">
              {t('auth.passwordReset.request.help')}
            </p>

            <div className="form-group">
              <button className="btn login-btn" disabled={isSubmitting}>
                {isSubmitting && <span className="spinner-border spinner-border-sm"></span>}
                <span>
                  {isSubmitting
                    ? t('auth.passwordReset.request.actions.submitting')
                    : t('auth.passwordReset.request.actions.submit')}
                </span>
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="auth-status auth-status--success" role="status" aria-live="polite">
              {t('auth.passwordReset.request.feedback.genericSuccess')}
            </div>
            <p className="auth-support-copy">
              {t('auth.passwordReset.request.descriptions.success')}
            </p>
            <div className="auth-secondary-actions">
              <button
                type="button"
                className="auth-secondary-button"
                onClick={() => setRequestSent(false)}
              >
                {t('auth.passwordReset.request.actions.useDifferentEmail')}
              </button>
            </div>
          </>
        )}

        <p className="login-footer">
          {t('auth.passwordReset.request.footer.prompt')}{' '}
          <Link to="/login">{t('auth.passwordReset.request.footer.link')}</Link>
        </p>
      </LiquidGlassCard>
    </>
  );
};

export default ForgotPasswordCard;
